import { useMemo } from 'react';
import { useTokenRoles } from './useTokenRoles';
import {
  ACTION_ROLE_MAP,
  getActionTooltip,
  ROLES,
  type PermissionAction,
} from '@/hooks/permissionConstants';

export { ROLES, ACTION_ROLE_MAP, getActionTooltip };
export type { PermissionAction };

export interface UsePermissionsReturn {
  /**
   * Raw roles from the user's access token
   */
  roles: string[];

  /**
   * Normalized permissions from roles and scopes
   */
  permissions: string[];
  
  /**
   * Array of member identifiers from the user's access token
   */
  memberIdentifiers: string[];
  
  /**
   * Whether the token roles are currently being loaded
   */
  isLoading: boolean;
  
  /**
   * Error message if token decoding failed
   */
  error: string | null;
  
  /**
   * Check if user has a specific role
   * @param role - The role to check
   * @returns True if user has the role
   */
  hasRole: (role: string) => boolean;
  
  /**
   * Check if user has any of the specified roles
   * @param requiredRoles - Array of roles to check
   * @returns True if user has at least one of the roles
   */
  hasAnyRole: (requiredRoles: string[]) => boolean;
  
  /**
   * Check if user has all of the specified roles
   * @param requiredRoles - Array of roles to check
   * @returns True if user has all the roles
   */
  hasAllRoles: (requiredRoles: string[]) => boolean;
  
  /**
   * Check if user can perform a specific action
   * Convenience methods for common permission checks
   */
  can: {
    // BOL Logistics Objects
    createLogisticsObject: boolean;
    readLogisticsObject: boolean;
    updateLogisticsObject: boolean;
    deleteLogisticsObject: boolean;
    
    // BOL Members
    createMember: boolean;
    readMember: boolean;
    deleteMember: boolean;
    
    // BOL Hosts
    createHost: boolean;
    readHost: boolean;
    deleteHost: boolean;
    
    // BOL Notifications
    manageSubscriptions: boolean;
    readSubscription: boolean;
  };
}

/**
 * Hook for checking user permissions based on roles in the access token
 * 
 * @example
 * ```tsx
 * const { hasRole, hasAnyRole, can } = usePermissions();
 * 
 * // Check specific role
 * if (hasRole(ROLES.BOL.LOGISTICS_OBJECTS.CREATE)) {
 *   // Show create button
 * }
 * 
 * // Check any of multiple roles
 * if (hasAnyRole([ROLES.BOL.MEMBERS.READ, ROLES.BOL.HOSTS.READ])) {
 *   // Show BOL menu
 * }
 * 
 * // Use convenience methods
 * <Button disabled={!can.createLogisticsObject}>Create</Button>
 * ```
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { roles, permissions, memberIdentifiers, isLoading, error } = useTokenRoles();

  // Memoize permission check functions to prevent unnecessary re-renders
  const hasRole = useMemo(
    () => (role: string): boolean => permissions.includes(role),
    [permissions]
  );

  const hasAnyRole = useMemo(
    () => (requiredRoles: string[]): boolean => {
      if (requiredRoles.length === 0) return true;
      return requiredRoles.some(role => permissions.includes(role));
    },
    [permissions]
  );

  const hasAllRoles = useMemo(
    () => (requiredRoles: string[]): boolean => {
      if (requiredRoles.length === 0) return true;
      return requiredRoles.every(role => permissions.includes(role));
    },
    [permissions]
  );

  // Convenience permission checks
  const can = useMemo(
    () => ({
      // BOL Logistics Objects
      createLogisticsObject: hasRole(ACTION_ROLE_MAP.createLogisticsObject),
      readLogisticsObject: hasRole(ACTION_ROLE_MAP.readLogisticsObject),
      updateLogisticsObject: hasRole(ACTION_ROLE_MAP.updateLogisticsObject),
      deleteLogisticsObject: hasRole(ACTION_ROLE_MAP.deleteLogisticsObject),
      
      // BOL Members
      createMember: hasRole(ACTION_ROLE_MAP.createMember),
      readMember: hasRole(ACTION_ROLE_MAP.readMember),
      deleteMember: hasRole(ACTION_ROLE_MAP.deleteMember),
      
      // BOL Hosts
      createHost: hasRole(ACTION_ROLE_MAP.createHost),
      readHost: hasRole(ACTION_ROLE_MAP.readHost),
      deleteHost: hasRole(ACTION_ROLE_MAP.deleteHost),
      
      // BOL Notifications
      manageSubscriptions: hasRole(ACTION_ROLE_MAP.manageSubscriptions),
      readSubscription: hasRole(ACTION_ROLE_MAP.readSubscription),
    }),
    [hasRole]
  );

  return {
    roles,
    permissions,
    memberIdentifiers,
    isLoading,
    error,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    can,
  };
};
